<template>
  <div>
    <div class="white-section mt-1" style="padding: 10px 20px">
      <div class="d-flex justify-content-end" style="align-items: center">
        <div style="margin-left: auto" class="page-title">
          <b-breadcrumb-item
            v-for="item in breadcrumb"
            :key="item.text"
            :active="item.active"
            class="bread-crumb"
          >
            <div v-if="item.active">
              {{ item.translate ? $t(`navigation.${item.text}`) : item.text }}
            </div>
            <!-- {{$route.meta}} -->
            <!-- {{ item.text }} -->
          </b-breadcrumb-item>
        </div>
        <b-row class="mt-1 search-box">
          <b-col md="6">
            <!-- <b-form-group
                  :label="$t('general.search')"
                  label-for="unit-unitName">
              </b-form-group> -->
            <div class="form-group">
              <b-form-input
                v-model="searchTerm"
                :placeholder="$t('general.searchByName')"
                type="text"
                class="d-inline-block"
                @input="searchUnit"
              />
            </div>
          </b-col>
          <b-col md="6">
            <div class="form-group">
              <MyVSelect
                :options="sections"
                v-model="searchedSection"
                :key="searchedSection"
                custom-class="w-100 mr-2"
                @input="searchUnitBySection"
              />
            </div>
          </b-col>
        </b-row>
        <router-link
          :disabled="
            !$ability.can('create', 'units') &&
            !$ability.can('read', 'superuser')
          "
          tag="button"
          :to="{ name: 'addUnit' }"
          class="btn btn-success btn-add"
          >{{ $t("general.add") }}</router-link
        >
      </div>

      <!-- table -->
      <vue-good-table
        class="user-table"
        :columns="columns"
        :rows="units"
        :search-options="{
          enabled: false,
          externalQuery: searchTerm,
        }"
        :select-options="{
          enabled: true,
          selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
          selectionInfoClass: 'custom-class',
          selectionText: 'rows selected',
          clearSelectionText: 'clear',
          disableSelectInfo: true, // disable the select info panel on top
          selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
          mode: 'pages',
        }"
      >
        <!-- Slot: Table Column -->
        <template slot="table-column" slot-scope="props">
          <span v-if="props.column.label === 'Status'" class="text-nowrap">
            {{ $t("message.tableHeader.status") }}
          </span>
          <span v-else-if="props.column.label === 'Action'" class="text-nowrap">
            {{ $t("message.tableHeader.action") }}
          </span>
          <span v-else>
            {{ props.column.label }}
          </span>
        </template>

        <!-- Slot: Table Row -->
        <template slot="table-row" slot-scope="props">
          <!-- Column: Action -->
          <span v-if="props.column.field === 'action'">
            <span>
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item
                  :disabled="
                    !$ability.can('update', 'units') &&
                    !$ability.can('read', 'superuser')
                  "
                  @click="editUnit(props.row)"
                >
                  <feather-icon icon="Edit2Icon" class="mr-50" />
                  <span>{{ $t("general.edit") }}</span>
                </b-dropdown-item>
                <b-dropdown-item
                  :disabled="
                    !$ability.can('delete', 'units') &&
                    !$ability.can('read', 'superuser')
                  "
                  @click="removeUnit(props.row)"
                >
                  <feather-icon icon="TrashIcon" class="mr-50" />
                  <span>{{ $t("general.delete") }}</span>
                </b-dropdown-item>
              </b-dropdown>
              <b-form-checkbox
                @input="switchStatus($event, props.row)"
                switch
                inline
                :checked="props.row.status == 'active'"
              >
                {{ $t(`general.${props.row.status}`) }}
              </b-form-checkbox>
            </span>
          </span>
          <!-- Column: Common -->
          <span
            v-else-if="
              props.column.field === 'sectionId' && props.row.sectionId
            "
          >
            {{ props.row.getSection.title }}
          </span>
          <span
            v-else-if="props.column.field === 'createAutomaticRecallNumber'"
          >
            {{
              props.row.createAutomaticRecallNumber == true
                ? $t("unit.createAutomaticRecallNumber")
                : $t("unit.createCustomRecallNumber")
            }}
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap">
                {{ $t("general.pageLength") }}
              </span>
              <b-form-select
                v-model="pageLength"
                :options="['20', '50', '100']"
                class="mx-1"
              />
              <span class="text-nowrap">
                {{ $t("general.of") }} {{ total }} {{ $t("general.pageText2") }}
              </span>
            </div>
            <div>
              <b-pagination
                v-model="page"
                :total-rows="total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => (this.page = value)"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>

<script>
import { BBreadcrumb, BBreadcrumbItem, BButton } from "bootstrap-vue";
import { mapGetters } from "vuex";
import {
  BAvatar,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BAlert,
  BDropdown,
  BDropdownItem,
  BFormCheckbox,
  BRow,
  BCol,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import store from "@/store";
import MyVSelect from "@/components/wrapper/select/MyVSelect";

export default {
  components: {
    MyVSelect,
    BBreadcrumb,
    BBreadcrumbItem,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BAlert,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    BRow,
    BCol,
  },
  data() {
    return {
      pageLength: 100,
      page: 1,
      dir: false,
      columns: [
        {
          label: this.$t("unit.unitTitle"),
          field: "title",
        },
        {
          label: this.$t("section.section"),
          field: "sectionId",
        },
        {
          label: this.$t("general.action"),
          field: "action",
        },
      ],
      searchTerm: "",
      searchedSection: "",
    };
  },
  watch: {
    page() {
      if (this.searchTerm) {
        this.$store.dispatch("units/searchUnit", {
          title: this.searchTerm,
          page: parseInt(this.page),
          limit: parseInt(this.pageLength),
        });
      } else {
        this.$store.dispatch("units/getUnits", {
          page: parseInt(this.page),
          limit: parseInt(this.pageLength),
        });
      }
    },
    pageLength() {
      if (this.searchTerm) {
        this.$store.dispatch("units/searchUnit", {
          title: this.searchTerm,
          page: parseInt(this.page),
          limit: parseInt(this.pageLength),
        });
      } else {
        this.$store.dispatch("units/getUnits", {
          page: parseInt(this.page),
          limit: parseInt(this.pageLength),
        });
      }
    },
  },
  methods: {
    switchStatus(val, props) {
      val === true ? (props.status = "active") : (props.status = "inactive");
      let payload = {
        status: props.status,
      };
      this.$store
        .dispatch("units/updateUnit", {
          id: props._id,
          payload: payload,
        })
        .then(() => {})
        .catch((error) => {});
    },

    removeItem(index) {
      this.units.splice(index, 1);
    },
    removeUnit(row) {
      this.$bvModal
        .msgBoxConfirm(this.$t("general.confirmDelete"), {
          title: this.$t("general.confirmDeleteTitle"),
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: this.$t("general.yes"),
          cancelTitle: this.$t("general.no"),
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((value) => {
          if (value === true) {
            this.$store.commit("SET_IS_LOADING", true);
            this.$store
              .dispatch("units/deleteUnit", { unitsId: row._id })
              .then(() => {
                this.$bvToast.toast(this.$t("toastification.deletionSucceed"), {
                  title: this.$t("toastification.success"),
                  variant: "success",
                  solid: true,
                });
                this.removeItem(row.originalIndex);
                this.$store.commit("SET_IS_LOADING", false);
              })
              .catch((error) => {
                let msg = "";
                switch (error.response.status) {
                  case 422:
                    msg = this.$t("toastification.deletionFailed");
                    break;

                  case 404:
                    msg = this.$t("toastification.deletionFailed");
                    break;

                  case 400:
                    msg = this.$t("toastification.badRequest");
                    break;
                }

                this.$bvToast.toast(msg, {
                  title: this.$t("toastification.error"),
                  variant: "danger",
                  solid: true,
                });
                this.$store.commit("SET_IS_LOADING", false);
              });
          }
        })
        .catch((err) => {
          alert("error");
        });
    },

    editUnit(unit) {
      this.$store.commit("units/SET_UNIT_TO_EDIT", unit);
      this.$router.push({ name: "editUnit", params: { id: unit._id } });
    },
    searchUnit(val) {
      if (val.length) {
        this.$store.dispatch("units/searchUnit", {
          title: val,
          page: parseInt(this.page),
          limit: parseInt(this.pageLength),
        });
      } else {
        this.$store.dispatch("units/searchUnit", {
          status:"active"
        });
      }
    },
    searchUnitBySection() {
      if (this.searchedSection) {
        this.$store.dispatch("units/searchUnit", {
          sectionId: this.searchedSection,
          page: parseInt(this.page),
          limit: parseInt(this.pageLength),
        });
      } else {
        this.$store.dispatch("units/searchUnit", {
          status:"active"
        });
      }
    },
  },
  computed: {
    breadcrumb() {
      return typeof this.$route.meta.breadCrumb === "function"
        ? this.$route.meta.breadCrumb(this.$route)
        : this.$route.meta.breadCrumb;
    },
    ...mapGetters({
      units: "units/units",
      sections: "sections/sections",
    }),
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        Current: "light-primary",
        Professional: "light-success",
        Rejected: "light-danger",
        Resigned: "light-warning",
        Applied: "light-info",
        /* eslint-enable key-spacing */
      };

      return (status) => statusColor[status];
    },
    total: {
      get() {
        return this.$store.getters["units/total"];
      },
    },
  },
  created() {
    this.$store.commit("SET_IS_LOADING", true);
    this.$store.dispatch("units/searchUnit", {
          status:"active"
        });
    this.$store
      .dispatch("units/searchUnit", { status: "active" , page: parseInt(this.page),
          limit: parseInt(this.pageLength), })
      .then((res) => {
        this.total=res.total
        this.$store.commit("SET_IS_LOADING", false);
      });
    this.setBreadcrumbs([
      { text: "homePage", translate: true, to: { name: "home" } },
      { text: "baseInformation", translate: true },
      { text: "unit", to: { name: "manageUnit" }, translate: true },
    ]);
    this.setCurrentBreadcrumb([
      { text: "unit", to: { name: "unit" }, translate: true },
    ]);
  },
};
</script>
